/**
 * useDocumentGenerator Hook
 * Manages document generation state and operations
 */
import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import documentService from '../api/documentService';

/**
 * Hook for document generation with state management
 *
 * @returns {Object} Document generation state and methods
 */
export const useDocumentGenerator = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);

  /**
   * Generate document using universal endpoint
   */
  const generateDocument = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    setResult(null);
    setProgress('Sending request to Claude API...');

    try {
      toast.info('Document generation started...', { autoClose: 2000 });

      const data = await documentService.generateDocument(payload);

      setResult(data);
      setProgress('Document generated successfully!');

      toast.success(`Document generated successfully! (${data.file_type.toUpperCase()})`, {
        autoClose: 3000,
      });

      // Show billing info
      if (data.billing) {
        if (data.billing.charged) {
          toast.info(`Charged: £${data.billing.amount}`, { autoClose: 5000 });
        } else {
          toast.info(`Free manual! ${data.billing.free_remaining} remaining`, { autoClose: 5000 });
        }
      }

      // Show token usage (for debugging)
      if (data.claude_metadata?.tokens_used) {
        console.log('Claude tokens used:', data.claude_metadata.tokens_used);
      }

      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to generate document';
      setError(errorMessage);
      setProgress(null);

      toast.error(`${errorMessage}`, { autoClose: 5000 });

      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Generate Level-1 Manual
   */
  const generateLevel1Manual = useCallback(async (companyId, isoStandardId, additionalParams) => {
    return generateDocument({
      company_id: companyId,
      iso_standard_id: isoStandardId,
      document_type: 'level1_manual',
      output_format: 'docx',
      additional_params: additionalParams,
    });
  }, [generateDocument]);

  /**
   * Generate Implementation Manual
   */
  const generateImplementationManual = useCallback(async (companyId, isoStandardId, timeline = '6') => {
    return generateDocument({
      company_id: companyId,
      iso_standard_id: isoStandardId,
      document_type: 'implementation_manual',
      output_format: 'docx',
      additional_params: { timeline },
    });
  }, [generateDocument]);

  /**
   * Generate Form
   */
  const generateForm = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    setResult(null);
    setProgress('Generating form...');

    try {
      toast.info('Generating form...', { autoClose: 2000 });

      const data = await documentService.generateForm(payload);

      setResult(data);
      setProgress('Form generated successfully!');

      toast.success(`Form ${payload.formName} generated!`, { autoClose: 3000 });

      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to generate form';
      setError(errorMessage);
      setProgress(null);

      toast.error(`${errorMessage}`, { autoClose: 5000 });

      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Generate Procedure
   */
  const generateProcedure = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    setResult(null);
    setProgress('Generating procedure...');

    try {
      toast.info('Generating procedure...', { autoClose: 2000 });

      const data = await documentService.generateProcedure(payload);

      setResult(data);
      setProgress('Procedure generated successfully!');

      toast.success(`Procedure ${payload.procedureName} generated!`, { autoClose: 3000 });

      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to generate procedure';
      setError(errorMessage);
      setProgress(null);

      toast.error(`${errorMessage}`, { autoClose: 5000 });

      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Generate Implementation Guide
   */
  const generateImplementationGuide = useCallback(async (companyId, isoStandardId, timeline = '6') => {
    return generateDocument({
      company_id: companyId,
      iso_standard_id: isoStandardId,
      document_type: 'implementation_guide',
      output_format: 'docx',
      additional_params: { timeline },
    });
  }, [generateDocument]);

  /**
   * Download generated document
   */
  const downloadDocument = useCallback(async (documentId, companyId) => {
    try {
      toast.info('Downloading document...', { autoClose: 2000 });

      await documentService.triggerDownload(documentId, companyId);

      toast.success('Download started!', { autoClose: 2000 });
    } catch (err) {
      toast.error(`Download failed: ${err.message}`, { autoClose: 5000 });
      throw err;
    }
  }, []);

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setResult(null);
    setProgress(null);
  }, []);

  return {
    // State
    loading,
    error,
    result,
    progress,

    // Methods
    generateDocument,
    generateLevel1Manual,
    generateImplementationManual,
    generateForm,
    generateProcedure,
    generateImplementationGuide,
    downloadDocument,
    reset,
  };
};

export default useDocumentGenerator;
